<template>
  <nav class="stella-bottom-tab-bar" aria-label="Mobile Navigation">
    <div class="stella-tab-container">
      <template v-for="(tab, index) in tabs">
        <!-- Route Link Tab -->
        <button
          v-if="tab.route"
          :key="index"
          type="button"
          class="stella-tab-button"
          :class="{ 'is-active': isTabActive(tab) }"
          @click="navigate(tab)"
          :aria-label="tab.name"
        >
          <div class="stella-tab-icon-wrapper">
            <component
              :is="tab.icon"
              :filled="isTabActive(tab)"
              class="stella-tab-svg"
            />
            <!-- Unread / Pending Badge -->
            <span
              v-if="getBadgeCount(tab) > 0"
              class="stella-tab-badge"
            >
              {{ getBadgeCount(tab) > 99 ? '99+' : getBadgeCount(tab) }}
            </span>
            <!-- Active Indicator Pill -->
            <span v-if="isTabActive(tab)" class="stella-tab-active-dot"></span>
          </div>
          <span class="stella-tab-label">{{ tab.name }}</span>
        </button>

        <!-- Action Tab (e.g. More / Profile) -->
        <button
          v-else
          :key="index"
          type="button"
          class="stella-tab-button"
          :class="{ 'is-active': isMoreActive }"
          @click="handleAction(tab)"
          :aria-label="tab.name"
        >
          <div class="stella-tab-icon-wrapper">
            <component
              :is="tab.icon"
              :filled="isMoreActive"
              class="stella-tab-svg"
            />
            <span v-if="isMoreActive" class="stella-tab-active-dot"></span>
          </div>
          <span class="stella-tab-label">{{ tab.name }}</span>
        </button>
      </template>
    </div>
  </nav>
</template>

<script>
import {
  IconHome,
  IconMessages,
  IconRequests,
  IconContent,
  IconProfile,
  IconMore
} from './icons';

export default {
  name: "BottomTabBar",
  components: {
    IconHome,
    IconMessages,
    IconRequests,
    IconContent,
    IconProfile,
    IconMore
  },
  props: {
    tabs: {
      type: Array,
      required: true
    },
    messageCount: {
      type: Number,
      default: 0
    },
    pendingRequestsCount: {
      type: Number,
      default: 0
    },
    isMoreActive: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    isTabActive(tab) {
      if (!tab.route) return false;
      if (this.$route.name === tab.route) return true;
      if (tab.activeRoutes && tab.activeRoutes.includes(this.$route.name)) {
        return true;
      }
      return false;
    },
    getBadgeCount(tab) {
      if (tab.name === 'Messages') {
        return this.messageCount;
      }
      if (tab.name === 'Requests') {
        return this.pendingRequestsCount;
      }
      return 0;
    },
    navigate(tab) {
      if (this.$route.name !== tab.route) {
        this.$router.push({ name: tab.route }).catch(err => {
          // Ignore duplicate navigation
          if (err && err.name !== 'NavigationDuplicated') {
            console.error(err);
          }
        });
      }
    },
    handleAction(tab) {
      if (tab.action === 'more') {
        this.$emit('toggle-more');
      } else {
        this.$emit('action', tab.action);
      }
    }
  }
}
</script>

<style scoped>
.stella-bottom-tab-bar {
  display: none;
}

@media (max-width: 991.98px) {
  .stella-bottom-tab-bar {
    display: block;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    height: calc(56px + env(safe-area-inset-bottom, 0px));
    padding-bottom: env(safe-area-inset-bottom, 0px);
    background: rgba(12, 12, 16, 0.88);
    backdrop-filter: blur(24px);
    -webkit-backdrop-filter: blur(24px);
    border-top: 1px solid rgba(255, 255, 255, 0.12);
    z-index: 1040;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.4);
    touch-action: manipulation;
    user-select: none;
  }

  .stella-tab-container {
    display: flex;
    align-items: center;
    justify-content: space-around;
    height: 56px;
    max-width: 600px;
    margin: 0 auto;
    padding: 0 8px;
  }

  .stella-tab-button {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    background: transparent;
    border: none;
    outline: none;
    color: rgba(255, 255, 255, 0.55);
    padding: 4px 0;
    cursor: pointer;
    position: relative;
    transition: color 0.2s ease, transform 0.15s ease;
    -webkit-tap-highlight-color: transparent;
  }

  .stella-tab-button:active {
    transform: scale(0.92);
  }

  .stella-tab-button.is-active {
    color: #ffffff;
  }

  .stella-tab-icon-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
  }

  .stella-tab-svg {
    width: 24px;
    height: 24px;
    transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  .stella-tab-button.is-active .stella-tab-svg {
    transform: scale(1.08);
    filter: drop-shadow(0 0 8px rgba(229, 59, 185, 0.45));
    color: #ffffff;
  }

  .stella-tab-label {
    font-size: 10px;
    font-weight: 500;
    margin-top: 2px;
    letter-spacing: 0.1px;
    transition: color 0.2s ease;
    line-height: 1.1;
  }

  .stella-tab-button.is-active .stella-tab-label {
    color: #ffffff;
    font-weight: 600;
  }

  .stella-tab-badge {
    position: absolute;
    top: -3px;
    right: -7px;
    background: linear-gradient(135deg, #E53BB9 0%, #FF3366 100%);
    color: #ffffff;
    font-size: 9.5px;
    font-weight: 700;
    min-width: 16px;
    height: 16px;
    line-height: 16px;
    border-radius: 10px;
    text-align: center;
    padding: 0 4px;
    box-shadow: 0 2px 6px rgba(229, 59, 185, 0.5);
    border: 1.5px solid #0c0c10;
  }

  .stella-tab-active-dot {
    position: absolute;
    bottom: -4px;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: #E53BB9;
    box-shadow: 0 0 6px #E53BB9;
  }
}
</style>
